import React, { useEffect, useState } from "react";
import { http } from "@/context/AuthContext";
import { Loader2, Plus, X } from "lucide-react";

function NewInvoiceDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({
    user_email: "",
    title: "",
    amount_dollars: "",
    status: "pending",
    due_at: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const payload = {
        user_email: form.user_email,
        title: form.title,
        amount_cents: Math.round(parseFloat(form.amount_dollars) * 100),
        status: form.status,
        due_at: form.due_at || null,
        notes: form.notes || null,
      };
      await http.post("/admin/invoices", payload);
      onSaved?.();
      onClose();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-navy/40 z-50 flex items-center justify-center p-4" data-testid="new-invoice-dialog">
      <form onSubmit={submit} className="bg-white rounded-3xl p-6 max-w-md w-full space-y-3 shadow-cardHover">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl text-navy">New Invoice</h3>
          <button type="button" onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-4 h-4" /></button>
        </div>
        <input required type="email" data-testid="invoice-email" placeholder="Client email" value={form.user_email} onChange={(e) => setForm({ ...form, user_email: e.target.value })} className="w-full border border-navy/15 rounded-xl px-4 py-2.5 text-sm" />
        <input required data-testid="invoice-title" placeholder="Title (e.g. Tax Prep — 2024)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-navy/15 rounded-xl px-4 py-2.5 text-sm" />
        <div className="grid grid-cols-2 gap-3">
          <input required type="number" step="0.01" data-testid="invoice-amount" placeholder="Amount ($)" value={form.amount_dollars} onChange={(e) => setForm({ ...form, amount_dollars: e.target.value })} className="border border-navy/15 rounded-xl px-4 py-2.5 text-sm" />
          <select data-testid="invoice-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="border border-navy/15 rounded-xl px-4 py-2.5 text-sm bg-white">
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>
        <input type="date" data-testid="invoice-due" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} className="w-full border border-navy/15 rounded-xl px-4 py-2.5 text-sm" />
        <textarea rows={3} placeholder="Notes (optional)" data-testid="invoice-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-navy/15 rounded-xl px-4 py-2.5 text-sm" />
        {err && <div className="text-red-500 text-xs">{err}</div>}
        <button type="submit" disabled={saving} data-testid="invoice-submit" className="btn-primary w-full disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Invoice
        </button>
      </form>
    </div>
  );
}

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  function refresh() {
    setLoading(true);
    http.get("/admin/invoices").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  return (
    <div data-testid="admin-invoices">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl">Invoices</h1>
          <p className="mt-2 text-navy/65">Client invoices across services. Bookipi-synced + manually created.</p>
        </div>
        <button onClick={() => setShowDialog(true)} data-testid="new-invoice-btn" className="btn-primary">
          <Plus className="w-4 h-4" /> Manual Invoice
        </button>
      </div>
      <div className="mt-8">
        {loading ? <div className="flex items-center gap-2 text-navy/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> :
        items.length === 0 ? <div className="card p-10 text-center text-navy/65">No invoices yet. They&apos;ll appear here as you issue them.</div> :
        <ul className="card divide-y divide-navy/5">
          {items.map((inv, i) => (
            <li key={inv.invoice_id || i} className="p-4 flex items-center justify-between text-sm" data-testid={`invoice-row-${i}`}>
              <div>
                <div className="text-navy font-medium">{inv.title || inv.service_name}</div>
                <div className="text-xs text-navy/55">${(inv.amount_cents || 0) / 100} · {inv.user_email || `user ${inv.user_id}`}{inv.source === "manual" && " · manual"}</div>
              </div>
              <span className={`text-xs uppercase tracking-widest ${inv.status === "paid" ? "text-teal" : inv.status === "overdue" ? "text-red-500" : "text-gold"}`}>{inv.status}</span>
            </li>
          ))}
        </ul>}
      </div>
      {showDialog && <NewInvoiceDialog onClose={() => setShowDialog(false)} onSaved={refresh} />}
    </div>
  );
}
